import React from 'react'
import PropTypes from 'prop-types'
import { Autocomplete } from 'transfix-ui/components/Autocomplete'
import SuggestLocation from 'transfix-ui/components/SuggestLocation'
import { Calendar, Checkbox, Time } from 'components/forms'
import { recommendBounceReasons } from 'constants/carrierBounceReasons'
import { LATE_REASONS } from 'constants/lateReasons'
import { LEAVING_LABELS, PRE_PICKUP_LABELS, KEYED_SHIPMENT_STATUSES } from 'constants/shipments'

export const LateReason = React.memo(({ lateReason, onChangeLateReason }) => (
  <div className='col-xs-9'>
    <Autocomplete
      label='Late Reason'
      id='late-reason'
      dataTest='status-update-dialog-late-reason'
      name={lateReason}
      getOptionLabel={options => options.label}
      onChange={onChangeLateReason}
      placeholder='Select a Late Reason'
      options={LATE_REASONS}
      required
    />
  </div>
))

export const Unresponsive = React.memo(({ onChangeUnresponsive, unresponsive }) => (
  <div className='col-xs-12'>
    <Checkbox
      label='Unresponsive Carrier'
      id='unresponsive'
      name='Unresponsive'
      checked={unresponsive}
      value={unresponsive}
      onChange={onChangeUnresponsive}
      dataTest='status-update-unresponsive-checkbox'
    />
  </div>
))

export const Detention = React.memo(({ detention, nextStatus, onChangeDetention }) => {
  if (!LEAVING_LABELS.includes(nextStatus)) return null
  return (
    <div className='col-xs-12'>
      <Checkbox
        label='Entering Detention'
        id='detention'
        name='Detention'
        checked={detention}
        value={detention}
        onChange={onChangeDetention}
        dataTest='status-update-detention-checkbox'
      />
    </div>
  )
})

export const RecommendBounce = React.memo(
  ({
    checkRecommendBounce,
    onChangeRecommendBounceReason,
    recommendBounceReason,
    recommendBounceSelected,
  }) => (
    <div className='col-xs-12 mt2'>
      <Checkbox
        label='Recommend Bounce'
        id='recommendBounce'
        name='RecommendBounce'
        checked={recommendBounceSelected}
        onChange={checkRecommendBounce}
        dataTest='status-update-recommended-bounce-checkbox'
      />
      {recommendBounceSelected && (
        <Autocomplete
          className='mt2'
          name='recommendBounce'
          options={recommendBounceReasons}
          value={recommendBounceReason}
          onChange={onChangeRecommendBounceReason}
          defaultText='Select Recommend Bounce Reason'
          required
          getOptionLabel={option => option.label}
          dataTest='status-update-dialog-recommend-bounce-reason'
        />
      )}
    </div>
  )
)

export const Eta = React.memo(
  ({
    checkRecommendBounce,
    detention,
    dropOrTrusted,
    etaDate,
    etaLate,
    etaTime,
    lateReason,
    nextStatus,
    onChangeDate,
    onChangeDetention,
    onChangeLateReason,
    onChangeRecommendBounceReason,
    onChangeTime,
    onChangeUnresponsive,
    recommendBounceReason,
    recommendBounceSelected,
    trackingUpdate,
    unresponsive,
  }) => {
    let label = 'Delivery ETA'
    let etaRequired
    if (nextStatus === KEYED_SHIPMENT_STATUSES.dispatched.label || trackingUpdate) {
      label = trackingUpdate ? 'ETA' : 'Pickup ETA'
      etaRequired = true
      if (dropOrTrusted || unresponsive || detention) etaRequired = false
    }
    const showDetention = trackingUpdate && LEAVING_LABELS.includes(nextStatus)
    const showRecommendBounce = trackingUpdate && PRE_PICKUP_LABELS.includes(nextStatus)

    return (
      <div className='row pv2'>
        <div className='flex items-end'>
          <div className='col-xs-4'>
            <Calendar
              value={etaDate}
              size='small'
              label={label}
              placeholder='Date'
              options={{ enableTime: false, dateFormat: 'm/d/Y' }}
              date={etaDate}
              dateTimeChangeToState={onChangeDate}
              required={etaRequired}
              disabled={unresponsive}
              dataTest='status-update-dialog-eta-date'
            />
          </div>
          <div className='col-xs-3 col-lg-3'>
            <Time
              id='etaTime'
              name='etaTime'
              value={etaTime}
              onChange={onChangeTime}
              required
              disabled={unresponsive}
              dataTest='status-update-dialog-eta-time'
            />
          </div>
        </div>
        {etaLate && !unresponsive && (
          <LateReason lateReason={lateReason} onChangeLateReason={onChangeLateReason} />
        )}
        {trackingUpdate && (
          <Unresponsive onChangeUnresponsive={onChangeUnresponsive} unresponsive={unresponsive} />
        )}
        {showDetention && (
          <Detention
            onChangeDetention={onChangeDetention}
            detention={detention}
            nextStatus={nextStatus}
          />
        )}
        {showRecommendBounce && (
          <RecommendBounce
            onChangeRecommendBounceReason={onChangeRecommendBounceReason}
            recommendBounceReason={recommendBounceReason}
            nextStatus={nextStatus}
            checkRecommendBounce={checkRecommendBounce}
            recommendBounceSelected={recommendBounceSelected}
          />
        )}
      </div>
    )
  }
)

export const LocationAndMiles = React.memo(
  ({ location, miles, onChangeLocation, onChangeMiles }) => (
    <div className='row'>
      <div className='col-xs-9 col-lg-9'>
        <SuggestLocation
          label='Location'
          placeholder='Location'
          value={location}
          dataTest='status-update-dialog-location'
          onChange={onChangeLocation}
        />
      </div>
      <div className='col-xs-3'>
        <label htmlFor='miles'>Miles</label>
        <input
          name='miles'
          value={miles}
          id='miles'
          type='text'
          maxLength='4'
          onChange={onChangeMiles}
          data-test='status-update-dialog-miles'
        />
      </div>
    </div>
  )
)

LateReason.propTypes = {
  lateReason: PropTypes.string.isRequired,
  onChangeLateReason: PropTypes.func.isRequired,
}

Unresponsive.propTypes = {
  unresponsive: PropTypes.bool.isRequired,
  onChangeUnresponsive: PropTypes.func.isRequired,
}

Detention.propTypes = {
  detention: PropTypes.bool.isRequired,
  onChangeDetention: PropTypes.func.isRequired,
  nextStatus: PropTypes.string.isRequired,
}

RecommendBounce.propTypes = {
  recommendBounceReason: PropTypes.string.isRequired,
  onChangeRecommendBounceReason: PropTypes.func.isRequired,
  recommendBounceSelected: PropTypes.bool.isRequired,
  checkRecommendBounce: PropTypes.func.isRequired,
}

Eta.propTypes = {
  nextStatus: PropTypes.string.isRequired,
  dropOrTrusted: PropTypes.bool,
  etaDate: PropTypes.string.isRequired,
  etaLate: PropTypes.bool.isRequired,
  etaTime: PropTypes.string.isRequired,
  lateReason: PropTypes.string,
  onChangeDate: PropTypes.func.isRequired,
  onChangeLateReason: PropTypes.func,
  onChangeTime: PropTypes.func.isRequired,
  trackingUpdate: PropTypes.bool.isRequired,
  unresponsive: PropTypes.bool,
  onChangeUnresponsive: PropTypes.func,
  detention: PropTypes.bool,
  onChangeDetention: PropTypes.func,
  recommendBounceReason: PropTypes.string,
  recommendBounceSelected: PropTypes.bool,
  onChangeRecommendBounceReason: PropTypes.func,
  checkRecommendBounce: PropTypes.func,
}
Eta.defaultProps = {
  lateReason: '',
  dropOrTrusted: false,
  onChangeLateReason: () => {},
  unresponsive: false,
  onChangeUnresponsive: () => {},
  detention: false,
  onChangeDetention: () => {},
  recommendBounceReason: '',
  recommendBounceSelected: false,
  onChangeRecommendBounceReason: () => {},
  checkRecommendBounce: () => {},
}

LocationAndMiles.propTypes = {
  location: PropTypes.shape({}),
  miles: PropTypes.string,
  onChangeMiles: PropTypes.func.isRequired,
  onChangeLocation: PropTypes.func.isRequired,
}
LocationAndMiles.defaultProps = {
  location: {},
  miles: '',
}
